import { useCallback, useEffect, useState } from "react";
import { SecondApi } from "../api/secondApi";
import { getSecondApiLocale } from "./apiLocale";
import {
  AUTH_TOKEN_COOKIE_NAME,
  clearAuthTokenCookie,
  getAuthTokenCookie,
  setAuthTokenCookie,
} from "./authCookie";

const AUTH_EVENT = "nomio:auth-change";

function emitAuthChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_EVENT));
  }
}

/** Subscribe to login/logout across the app (incl. other tabs via storage-like cookie pings). */
export default function useAuth() {
  const [token, setToken] = useState(() => getAuthTokenCookie());

  useEffect(() => {
    const sync = () => setToken(getAuthTokenCookie());
    window.addEventListener(AUTH_EVENT, sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener(AUTH_EVENT, sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  const saveToken = useCallback((newToken) => {
    setAuthTokenCookie(newToken);
    setToken(newToken);
    emitAuthChange();
  }, []);

  const logout = useCallback(
    async (lang = "en") => {
      const apiLocale = getSecondApiLocale(lang);
      try {
        if (getAuthTokenCookie()) {
          await SecondApi.post(`/api/${apiLocale}/auth/logout`);
        }
      } catch (e) {
        // Even if the API call fails, we still clear the local session.
      } finally {
        clearAuthTokenCookie();
        setToken(null);
        emitAuthChange();
      }
    },
    [],
  );

  return {
    token,
    isAuthenticated: Boolean(token),
    saveToken,
    logout,
    cookieName: AUTH_TOKEN_COOKIE_NAME,
  };
}
